// src/components/core/FullScreenOverlay.jsx
//
// The full-screen window a form opens in, over the page it came from (My Articles' and Pending
// Events' edit windows). #78, docs/briefs/2026-09-28-edit-forms-truth-and-unsaved-warning.md
// addendum 1 item 1.
//
// It sits on tokens.zIndex.overlay, BELOW tokens.zIndex.modal, so every message a form inside it
// raises (core/Modal, ConfirmationModal) shows in front of it. It used to be a raw z-index 10000
// in MyArticles, which hid them all (LAYOUT § Z-index Scale). Scrim from tokens.colors.backdrop
// (LAYOUT § Backdrop). Portalled to document.body so no transformed ancestor can misplace it.
//
// The close button (and a click on the scrim, if a narrower panel ever exposes it) ask the form first when it has unsaved changes:
// a form using useUnsavedChanges registers its check through LeaveGuardContext. No Escape handler
// here, so Escape in a form's own dialog never also closes the window.

import { useCallback, useMemo, useRef } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import { tokens } from '../../styles/designTokens';
import { LeaveGuardContext } from '../../hooks/useUnsavedChanges';
import { useTranslation } from '../../i18n/useTranslation';

const FullScreenOverlay = ({ onClose, children, panelBackground = 'white' }) => {
  const { t } = useTranslation('common');
  const guardRef = useRef(null);

  const leaveGuard = useMemo(() => ({
    register: (guard) => {
      guardRef.current = guard;
      return () => { if (guardRef.current === guard) guardRef.current = null; };
    },
  }), []);

  const requestClose = useCallback(async () => {
    const guard = guardRef.current;
    if (guard && !(await guard())) return;
    onClose();
  }, [onClose]);

  return createPortal(
    <div style={{ position: 'fixed', inset: 0, zIndex: tokens.zIndex.overlay }}>
      {/* Scrim, per LAYOUT § Backdrop. The panel covers it today; kept so a narrower panel
          later still dims the page behind it. */}
      <div
        style={{ position: 'absolute', inset: 0, background: tokens.colors.backdrop }}
        onClick={requestClose}
      />
      <div style={{ position: 'relative', zIndex: 1, width: '100%', height: '100%', background: panelBackground, overflow: 'auto' }}>
        <button
          onClick={requestClose}
          aria-label={t('close')}
          style={{
            position: 'fixed', top: 12, right: 12, zIndex: tokens.zIndex.overlay + 1, // above the form's own fixed header, as PendingEvents did
            width: 36, height: 36, borderRadius: '50%', border: 'none',
            background: 'rgba(0, 0, 0, 0.6)', color: 'white', cursor: 'pointer',
            display: 'flex', alignItems: 'center', justifyContent: 'center'
          }}
        >
          <X size={20} />
        </button>
        <LeaveGuardContext.Provider value={leaveGuard}>
          {children}
        </LeaveGuardContext.Provider>
      </div>
    </div>,
    document.body
  );
};

export default FullScreenOverlay;
